import React, { useEffect, useMemo, useState } from 'react';
import { aiAPI } from '../../services/api';

const LENGTH_OPTIONS = [
    { key: 'short',  label: 'Kurz',  hint: 'Richtwert ~200' },
    { key: 'medium', label: 'Mittel', hint: 'Richtwert ~350' },
    { key: 'long',   label: 'Lang',  hint: 'Richtwert ~450' },
    { key: 'custom', label: 'Frei',  hint: 'Eigener Richtwert' },
];

const EVENT_ICONS = { 2: '⚽', 3: '🟨', 4: '🟥', 5: '🔄', 6: '⏸', 7: '🏁', 8: '💥' };

export default function ReportGenerator({ gameId, events }) {
    const [reportData, setReportData] = useState(null);
    const [report, setReport] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [error, setError] = useState('');
    const [copyDone, setCopyDone] = useState(false);
    const [copyDataDone, setCopyDataDone] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editText, setEditText] = useState('');
    const [length, setLength] = useState('medium');
    const [customWords, setCustomWords] = useState('500');

    const reportEvents = useMemo(
        () => (events || [])
            .filter(e => e.published !== false && e.reportFlags?.useInReport)
            .sort((a, b) => {
                const dm = (a.minute || 0) - (b.minute || 0);
                if (dm !== 0) return dm;
                return new Date(a.createdAt) - new Date(b.createdAt);
            }),
        [events]
    );

    const allEvents = (events || []).filter(e => e.published !== false);

    useEffect(() => {
        if (!gameId) return;
        setLoadingData(true);
        aiAPI.getReportData(gameId)
            .then(res => setReportData(res.data))
            .catch(err => setError(err.response?.data?.message || 'Spieldaten konnten nicht geladen werden'))
            .finally(() => setLoadingData(false));
    }, [gameId, events]);

    const generate = async () => {
        setLoading(true);
        setError('');
        setReport('');
        setEditMode(false);
        try {
            const body = { length };
            if (length === 'custom') body.wordCount = parseInt(customWords, 10) || 350;
            const res = await aiAPI.generateReport(gameId, body);
            setReport(res.data.report || '');
            setEditText(res.data.report || '');
            if (res.data.spieldaten) setReportData(prev => ({ ...prev, spieldaten: res.data.spieldaten }));
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = (text, setter) => {
        navigator.clipboard.writeText(text).then(() => {
            setter(true);
            setTimeout(() => setter(false), 2000);
        });
    };

    const activeText = editMode ? editText : report;

    return (
        <div className="flex flex-col gap-4 max-w-3xl">
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 flex items-start gap-3">
                <span className="text-2xl">🤖</span>
                <div>
                    <p className="font-semibold text-violet-800 text-sm">KI Spielbericht-Generator</p>
                    <p className="text-xs text-violet-600 mt-0.5">
                        {reportEvents.length} von {allEvents.length} Einträgen mit 📝 für Bericht markiert.
                        {reportEvents.length === 0 && (
                            <span className="text-amber-600 font-medium"> Tipp: 📝 für wichtige Events setzen.</span>
                        )}
                    </p>
                </div>
            </div>

            <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
                <div className="flex items-center justify-between px-4 py-2 bg-slate-800 text-white">
                    <span className="text-sm font-semibold">Spieldaten</span>
                    {reportData?.spieldaten && (
                        <button
                            type="button"
                            onClick={() => handleCopy(reportData.spieldaten, setCopyDataDone)}
                            className="btn btn-xs btn-ghost text-white"
                        >
                            {copyDataDone ? '✓ Kopiert' : '📋 Kopieren'}
                        </button>
                    )}
                </div>
                <div className="p-4">
                    {loadingData ? (
                        <span className="loading loading-spinner loading-sm" />
                    ) : reportData?.spieldaten ? (
                        <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">{reportData.spieldaten}</pre>
                    ) : (
                        <p className="text-sm text-slate-400">Keine Spieldaten — Aufstellung und Events ergänzen.</p>
                    )}
                </div>
            </div>

            {reportEvents.length > 0 && (
                <details className="bg-slate-50 border rounded-xl overflow-hidden" open>
                    <summary className="cursor-pointer px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100">
                        ⚡ Ereignis-Kurzliste — für Bericht markiert ({reportEvents.length})
                    </summary>
                    <div className="px-4 pb-3 flex flex-col gap-1 mt-1">
                        {reportEvents.map(e => (
                            <div key={e._id} className="flex gap-2 text-xs text-slate-600">
                                <span className="font-mono font-bold text-slate-400 w-8 flex-none">
                                    {e.minute > 0 ? `${e.minute}'` : '–'}
                                </span>
                                <span>
                                    {EVENT_ICONS[e.type] ? `${EVENT_ICONS[e.type]} ` : ''}
                                    {e.textPublished || e.textResolved || e.textRaw}
                                </span>
                            </div>
                        ))}
                    </div>
                </details>
            )}

            <div>
                <p className="text-xs font-semibold text-slate-600 mb-2">Berichtslänge (Richtwert, kein hartes Limit)</p>
                <div className="flex flex-wrap gap-2">
                    {LENGTH_OPTIONS.map(opt => (
                        <button
                            key={opt.key}
                            type="button"
                            onClick={() => setLength(opt.key)}
                            className={`btn btn-sm ${length === opt.key ? 'btn-primary' : 'btn-outline'}`}
                        >
                            {opt.label}
                            <span className="text-xs opacity-70 ml-1">{opt.hint}</span>
                        </button>
                    ))}
                </div>
                {length === 'custom' && (
                    <div className="mt-2 flex items-center gap-2">
                        <input
                            type="number"
                            min="80"
                            max="2000"
                            value={customWords}
                            onChange={e => setCustomWords(e.target.value)}
                            className="input input-bordered input-sm w-28"
                        />
                        <span className="text-xs text-slate-500">Wörter (Richtwert)</span>
                    </div>
                )}
            </div>

            <div className="flex gap-2 flex-wrap">
                <button type="button" onClick={generate} disabled={loading} className="btn btn-primary btn-sm gap-2">
                    {loading ? (
                        <><span className="loading loading-spinner loading-xs" /> Generiere…</>
                    ) : (
                        '🤖 Spielbericht generieren'
                    )}
                </button>
            </div>

            {error && <div className="alert alert-error text-sm"><span>⚠️ {error}</span></div>}

            {report && (
                <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
                    <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b">
                        <span className="text-sm font-semibold text-slate-700">📄 Generierter Spielbericht</span>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => { setEditMode(m => !m); setEditText(report); }}
                                className={`btn btn-xs ${editMode ? 'btn-primary' : 'btn-ghost'}`}
                            >
                                {editMode ? '👁 Vorschau' : '✏️ Bearbeiten'}
                            </button>
                            <button type="button" onClick={() => handleCopy(activeText, setCopyDone)} className="btn btn-xs btn-ghost">
                                {copyDone ? '✓ Kopiert!' : '📋 Kopieren'}
                            </button>
                        </div>
                    </div>
                    <div className="p-4">
                        {editMode ? (
                            <textarea
                                className="textarea textarea-bordered w-full text-sm leading-relaxed"
                                rows={18}
                                value={editText}
                                onChange={e => setEditText(e.target.value)}
                            />
                        ) : (
                            <div className="prose prose-sm max-w-none text-slate-800 leading-relaxed whitespace-pre-wrap">
                                {activeText}
                            </div>
                        )}
                    </div>
                    <div className="px-4 pb-4">
                        <button type="button" onClick={generate} disabled={loading} className="btn btn-xs btn-outline">
                            🔄 Neu generieren
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
